import type {
  RunDetailResponse,
  RunPage,
} from "contracts/api/crawl/run-read.responses";
import { CRAWL_FRONTIER_STATUS, CRAWL_RUN_STATUS } from "contracts/enums/core";
import { YEET_ATTEMPT_STATUS } from "contracts/enums/yeet";
import { createEffect, createSignal, For, on, Show } from "solid-js";
import { YEET_FAILURE_REASON_LABELS } from "../../enums/run-failures";
import { EmptyState } from "../../ui/EmptyState";
import { relativeAge } from "../../ui/format";
import { pageFailurePresentation } from "./runFailurePresentation";
import { attemptStatusLabel, runPageStageLabel } from "./runPresentation";
import { createRunRepair } from "./runQueries";

/**
 * Repair re-analyses a page against the capture it already holds, so only a
 * captured page is a candidate. A `failed` page never produced a capture — its
 * frontier row stays `scraped` when only the analysis failed — so it needs a
 * fresh run, not a repair, and the backend rejects the whole batch if one is
 * included.
 */
const isRepairCandidate = (page: RunPage) =>
  page.status === CRAWL_FRONTIER_STATUS.SCRAPED;

/** A captured page whose newest attempt did not produce analysis. */
const needsRepair = (page: RunPage) =>
  isRepairCandidate(page) &&
  page.attemptStatus !== YEET_ATTEMPT_STATUS.COMPLETED;

export function RunPagesTable(props: {
  websiteId: string;
  run: RunDetailResponse;
  pages: RunPage[];
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  const repair = createRunRepair(props.websiteId);
  const [selected, setSelected] = createSignal<Set<string>>(new Set());
  const candidates = () => props.pages.filter(isRepairCandidate);
  // Repair is a failure remedy, so the pages that failed analysis start
  // selected; anything else is an opt-in re-analysis.
  createEffect(
    on(
      () => props.run.runId,
      () =>
        setSelected(
          new Set(props.pages.filter(needsRepair).map((page) => page.url)),
        ),
    ),
  );
  const selectable = () =>
    props.run.status === CRAWL_RUN_STATUS.COMPLETED ||
    props.run.status === CRAWL_RUN_STATUS.FAILED;
  const allSelected = () =>
    candidates().length > 0 && selected().size === candidates().length;
  const toggle = (url: string, checked: boolean) => {
    const next = new Set(selected());
    if (checked) next.add(url);
    else next.delete(url);
    setSelected(next);
  };
  const toggleAll = (checked: boolean) => {
    setSelected(
      checked
        ? new Set<string>(candidates().map((page) => page.url))
        : new Set<string>(),
    );
  };
  const submit = () => {
    repair.mutate({ runId: props.run.runId, urls: [...selected()] });
  };

  return (
    <section class="run-pages-section">
      <div class="run-pages-heading">
        <h3>Pages</h3>
        <Show when={selectable()}>
          <button
            class="signal-scan-btn"
            type="button"
            disabled={selected().size === 0 || repair.isPending}
            onClick={submit}
          >
            {repair.isPending
              ? "Starting re-analysis…"
              : `Re-analyse selected (${selected().size})`}
          </button>
        </Show>
      </div>
      <Show when={repair.error}>
        {(error) => (
          <p class="signal-scan-error" role="alert">
            {error().message}
          </p>
        )}
      </Show>
      <Show
        when={props.pages.length > 0}
        fallback={<EmptyState title="No pages admitted yet" />}
      >
        <table class="tbl">
          <thead>
            <tr>
              <Show when={selectable()}>
                <th class="run-page-select">
                  <input
                    type="checkbox"
                    aria-label="Select every page"
                    checked={allSelected()}
                    onChange={(event) => toggleAll(event.currentTarget.checked)}
                  />
                </th>
              </Show>
              <th>URL</th>
              <th>Depth</th>
              <th>Page status</th>
              <th>Attempts</th>
              <th>Last activity</th>
              <th>Assessment</th>
            </tr>
          </thead>
          <tbody>
            <For each={props.pages}>
              {(page) => {
                const failure = () => pageFailurePresentation(page);
                return (
                  <tr>
                    <Show when={selectable()}>
                      <td class="run-page-select">
                        <input
                          type="checkbox"
                          aria-label={`Select ${page.url}`}
                          disabled={!isRepairCandidate(page)}
                          title={
                            isRepairCandidate(page)
                              ? undefined
                              : "No capture to re-analyse; this page needs a fresh scan"
                          }
                          checked={selected().has(page.url)}
                          onChange={(event) =>
                            toggle(page.url, event.currentTarget.checked)
                          }
                        />
                      </td>
                    </Show>
                    <td class="mono" title={page.url}>
                      {page.url}
                    </td>
                    <td class="td-num mono">{page.depth}</td>
                    <td>
                      <span class="badge">{runPageStageLabel(page)}</span>
                      <Show when={failure()}>
                        {(detail) => (
                          <small class="run-failure-reason">
                            {detail().detail}
                          </small>
                        )}
                      </Show>
                      <Show
                        when={
                          page.failureReason ||
                          page.lastError ||
                          page.attemptSummary ||
                          page.attemptFailureReason
                        }
                      >
                        <details class="run-technical-detail">
                          <summary>Technical detail</summary>
                          <dl>
                            <Show when={page.failureReason}>
                              {(reason) => (
                                <div>
                                  <dt>Failure reason</dt>
                                  <dd class="mono">{reason()}</dd>
                                </div>
                              )}
                            </Show>
                            <Show when={page.lastError}>
                              {(lastError) => (
                                <div>
                                  <dt>Worker output</dt>
                                  <dd class="mono">{lastError()}</dd>
                                </div>
                              )}
                            </Show>
                            <Show when={page.attemptSummary}>
                              {(summary) => (
                                <div>
                                  <dt>Agent output</dt>
                                  <dd>{summary()}</dd>
                                </div>
                              )}
                            </Show>
                            <Show when={page.attemptFailureReason}>
                              {(reason) => (
                                <div>
                                  <dt>Assessment failure reason</dt>
                                  <dd class="mono">
                                    {YEET_FAILURE_REASON_LABELS[reason()]} (
                                    {reason()})
                                  </dd>
                                </div>
                              )}
                            </Show>
                          </dl>
                        </details>
                      </Show>
                    </td>
                    <td class="td-num mono">{page.attempts}</td>
                    <td title={page.lastAttemptAt.toLocaleString()}>
                      {relativeAge(page.lastAttemptAt)}
                    </td>
                    <td>
                      <span class="badge">
                        {attemptStatusLabel(page.attemptStatus)}
                      </span>
                    </td>
                  </tr>
                );
              }}
            </For>
          </tbody>
        </table>
      </Show>
      <Show when={props.hasMore}>
        <button
          type="button"
          class="btn run-pages-more"
          disabled={props.loadingMore}
          onClick={() => props.onLoadMore()}
        >
          {props.loadingMore ? "Loading..." : "Load more pages"}
        </button>
      </Show>
    </section>
  );
}
